import { Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, of, take } from 'rxjs';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { InterviewMessageService } from '../interview-message/interview-message.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { InterviewGate, InterviewIdRequest } from './../../../../../backend/src/core/interview/interview.gate';
import { LambdaService } from './../../shared/core/lambda.service';
import { UserClerkService } from './../user/user-clerk.service';
import {
  Interview,
  InterviewStatus,
  InterviewSubData,
  InterviewType,
  SESSION_LENGTH,
  isCreditsEnoughForInterviewType,
} from './interview';

@Injectable({
  providedIn: 'root',
})
export class InterviewService {
  private _resourceName = ResourceName.interview;

  private _changeSubject = new BehaviorSubject<Interview | undefined>(undefined);

  tabMedia?: MediaStream;
  micStream?: MediaStream;

  showNotEnoughPointsModal = signal<boolean>(false);

  user = toSignal(this.userClerkService.getUser$());

  constructor(
    private coreItemService: CoreItemService,
    private lambdaService: LambdaService,
    private userClerkService: UserClerkService,
    private interviewMessageService: InterviewMessageService
  ) {}

  stopTabSharing() {
    this.tabMedia?.getTracks().forEach((track) => track.stop());
    this.tabMedia = undefined;
  }

  stopMicSharing() {
    this.micStream?.getTracks().forEach((track) => {
      track.stop();
    });
    this.micStream = undefined;
  }

  isCreditsEnough(interview: Interview) {
    const type = interview.type;
    const credits = this.user()?.publicMetadata['credits'] as number;
    return isCreditsEnoughForInterviewType(type, credits);
  }

  pingInterview$(interview: Interview) {
    if (interview.isPaid) {
      return of({
        isSuccess: true,
      });
    }

    const request: InterviewIdRequest = { id: interview.id };
    return this.lambdaService
      .run$(this._resourceName, InterviewGate.actions.pingInterview, request, { notChangeLoadingStatus: true })
      .pipe(
        map((data) => {
          console.log('pingInterview$:', data);

          if (data?.id && data?.sk) {
            const frontend = this.coreItemService.updateCoreItemInCache(data) as Interview;
            this._changeSubject.next(frontend);
            return {
              isSuccess: true,
            };
          } else {
            return data;
          }
        })
      );
  }

  interviewList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$([CoreItemType.interview]).pipe(
          map((list) => (list ?? []).filter((item) => item.coreType === CoreItemType.interview) as Interview[]),
          map((list) =>
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1))
          )
        );
      })
    );
  }

  getInterviewById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Interview));
  }

  isOngoing(interview: Interview) {
    if (interview.status && interview.startTime) {
      if (interview.status === InterviewStatus.done) {
        return false;
      }

      const diff = -interview.startTime.diffNow('minutes').minutes;

      if (interview.status === InterviewStatus.ongoing && diff <= SESSION_LENGTH) {
        return true;
      } else if (interview.status === InterviewStatus.ongoing && diff > SESSION_LENGTH) {
        interview.status = InterviewStatus.done;
        this.endInterview$(interview).pipe(take(1)).subscribe();
      }
    }
    return false;
  }

  launchInterview$(interview: Interview) {
    const request: InterviewIdRequest = {
      id: interview.id,
    };

    return this.lambdaService.run$(this._resourceName, InterviewGate.actions.launchInterview, request).pipe(
      map((response) => {
        const front = this.coreItemService.updateCoreItemInCache(response) as Interview;
        this._changeSubject.next(front);

        if (interview.type === InterviewType.normal) {
          posthog.capture(PostHogEvent.interviewNormalLaunch, { interviewId: response.id });
        } else if (interview.type === InterviewType.code) {
          posthog.capture(PostHogEvent.interviewCodingLaunch, { interviewId: response.id });
        }

        return front;
      })
    );
  }

  launchFreeTrialInterview$(interview: Interview) {
    const request: InterviewIdRequest = {
      id: interview.id,
    };

    return this.lambdaService.run$(this._resourceName, InterviewGate.actions.launchFreeTrialInterview, request).pipe(
      map((response) => {
        if (response) {
          if (response.id) {
            const front = this.coreItemService.updateCoreItemInCache(response) as Interview;
            this._changeSubject.next(front);

            if (interview.type === InterviewType.normal) {
              posthog.capture(PostHogEvent.interviewNormalFreeTrialLaunch, { interviewId: response.id });
            } else if (interview.type === InterviewType.code) {
              posthog.capture(PostHogEvent.interviewCodingFreeTrialLaunch, { interviewId: response.id });
            }

            this.userClerkService.reloadUser$().pipe(take(1)).subscribe();
          }
        }
        return response;
      })
    );
  }

  endInterview$(interview: Interview) {
    const request: InterviewIdRequest = {
      id: interview.id,
    };

    return this.lambdaService.run$(this._resourceName, InterviewGate.actions.endInterview, request).pipe(
      map((response) => {
        const front = this.coreItemService.updateCoreItemInCache(response) as Interview;
        this._changeSubject.next(front);
        return front;
      })
    );
  }

  addInterview$(type: InterviewType, subData: InterviewSubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            coreType: CoreItemType.interview,
            status: InterviewStatus.notStarted,
            statusTime: DateTime.local(),
            type,
            subData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          const interview = arr[0] as Interview;
          const interviewBackend = this.coreItemService.convertFromFrontendToBackend(interview);

          if (type === InterviewType.normal) {
            posthog.capture(PostHogEvent.interviewNormalAdd, { interviewId: interviewBackend.id });
          } else if (type === InterviewType.code) {
            posthog.capture(PostHogEvent.interviewCodingAdd, { interviewId: interviewBackend.id });
          }

          this._changeSubject.next(interview);
          return interview;
        })
      );
  }

  deleteInterview$(interview: Interview) {
    const deleteMetadataList = [interview].map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        mergeMap((_) => {
          posthog.capture(PostHogEvent.interviewDelete, {
            interviewId: interview.id,
            deleteCount: 1,
          });
          this._changeSubject.next(undefined);
          return this.interviewMessageService
            .getInterviewMessageListById$(interview.id)
            .pipe(mergeMap((dataList) => this.interviewMessageService.deleteInterviewMessageList$(dataList)));
        })
      );
  }
}
